import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const RolesPermissionsPage = () => {
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('roles'); // roles, permissions

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [rolesRes, categoriesRes, permissionsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/roles`, config),
        axios.get(`${API_URL}/api/rbac/categories`, config),
        axios.get(`${API_URL}/api/rbac/permissions/by-category`, config),
        axios.get(`${API_URL}/api/rbac/stats`, config)
      ]);

      setRoles(rolesRes.data);
      setCategories(categoriesRes.data);
      setPermissions(permissionsRes.data);
      setStats(statsRes.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId) => {
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      const response = await axios.get(`${API_URL}/api/rbac/roles/${roleId}/permissions`, config);
      setRolePermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      setRolePermissions([]);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    fetchRolePermissions(role.id);
  };

  const handlePermissionToggle = (permissionId) => {
    const isSelected = rolePermissions.some(p => p.id === permissionId);
    
    if (isSelected) {
      setRolePermissions(rolePermissions.filter(p => p.id !== permissionId));
    } else {
      const permission = permissions
        .flatMap(cat => cat.permissions)
        .find(p => p.id === permissionId);
      
      if (permission) {
        setRolePermissions([...rolePermissions, permission]);
      }
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setSaving(true);
      
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const permissionIds = rolePermissions.map(p => p.id);

      await axios.put(
        `${API_URL}/api/rbac/roles/${selectedRole.id}/permissions`,
        {
          role_id: selectedRole.id,
          permission_ids: permissionIds
        },
        config
      );

      alert('تم حفظ الصلاحيات بنجاح!');
      setSaving(false);
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('حدث خطأ أثناء حفظ الصلاحيات');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            إدارة الأدوار والصلاحيات
          </h1>
          <p className="text-gray-600">
            نظام التحكم في الصلاحيات (RBAC) - إدارة كاملة للأدوار والصلاحيات
          </p>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">الأدوار</p>
                <p className="text-2xl font-bold text-blue-900">{stats.total_roles}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">الصلاحيات</p>
                <p className="text-2xl font-bold text-green-900">{stats.total_permissions}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">المجموعات</p>
                <p className="text-2xl font-bold text-purple-900">{stats.total_categories}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-600 font-medium">موظفين مع أدوار</p>
                <p className="text-2xl font-bold text-orange-900">{stats.employees_with_roles}</p>
              </div>
              <div className="bg-pink-50 rounded-lg p-4">
                <p className="text-sm text-pink-600 font-medium">صلاحيات مخصصة</p>
                <p className="text-2xl font-bold text-pink-900">{stats.employees_with_custom_permissions}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('roles')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'roles'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                إدارة صلاحيات الأدوار
              </button>
              <button
                onClick={() => setActiveTab('permissions')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'permissions'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                عرض جميع الصلاحيات
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'roles' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Roles List */}
            <div className="col-span-12 md:col-span-4 bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">الأدوار المتاحة</h2>
              
              <div className="space-y-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role)}
                    className={`w-full text-right p-4 rounded-lg border-2 transition-all ${
                      selectedRole?.id === role.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                  >
                    <div className="font-bold text-gray-900">{role.name_ar}</div>
                    <div className="text-sm text-gray-500">{role.name_en}</div>
                    {role.is_system_role && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                        دور نظام
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Permissions Editor */}
            <div className="col-span-12 md:col-span-8 bg-white rounded-lg shadow-sm p-6">
              {!selectedRole ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <p className="mt-4 text-gray-500">اختر دوراً لتعديل صلاحياته</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedRole.name_ar}</h2>
                      <p className="text-sm text-gray-500">{selectedRole.description_ar}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        الصلاحيات الحالية: {rolePermissions.length}
                      </p>
                    </div>
                    <button
                      onClick={handleSavePermissions}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                    </button>
                  </div>

                  {/* Permissions by Category */}
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {permissions.map((categoryGroup) => (
                      <div key={categoryGroup.category.code} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-gray-900">{categoryGroup.category.name_ar}</h3>
                          <span className="text-xs text-gray-500">
                            {categoryGroup.permissions.filter(p => rolePermissions.some(rp => rp.id === p.id)).length} / {categoryGroup.permissions.length}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {categoryGroup.permissions.map((permission) => {
                            const isSelected = rolePermissions.some(p => p.id === permission.id);
                            
                            return (
                              <label
                                key={permission.id}
                                className={`flex items-start p-3 rounded border cursor-pointer transition-all ${
                                  isSelected
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'bg-gray-50 border-gray-200 hover:border-blue-200'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <div className="mr-3 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{permission.name_ar}</span>
                                    {permission.is_dangerous && (
                                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                        خطر
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">{permission.code}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">جميع الصلاحيات المتاحة</h2>
            
            <div className="space-y-6">
              {permissions.map((categoryGroup) => (
                <div key={categoryGroup.category.code} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">{categoryGroup.category.name_ar}</h3>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {categoryGroup.permissions.length} صلاحية
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categoryGroup.permissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="p-3 bg-gray-50 rounded border border-gray-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">{permission.name_ar}</p>
                            <p className="text-xs text-gray-500 mt-1">{permission.code}</p>
                          </div>
                          {permission.is_dangerous && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                              خطر
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesPermissionsPage;
